/* ============================================================
   208.GOLF — global.js
   Lightweight runtime: cart drawer, mobile nav, fetch helpers,
   custom elements for shared interactive components.
   No build step required.
   ============================================================ */

(function () {
  'use strict';

  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};

  /* ------------------------------------------------------------
     Cart API helpers
     ------------------------------------------------------------ */
  const Cart = {
    async get() {
      const res = await fetch(routes.cart, { headers: { Accept: 'application/json' } });
      return res.json();
    },
    async add(items) {
      const res = await fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] })
      });
      if (!res.ok) throw new Error('Cart add failed');
      return res.json();
    },
    async change(key, quantity) {
      const res = await fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      return res.json();
    }
  };
  window.theme = Object.assign({}, window.theme, { Cart });

  /* ------------------------------------------------------------
     <cart-count> — listens for cart:updated and rerenders
     ------------------------------------------------------------ */
  class CartCount extends HTMLElement {
    connectedCallback() {
      this.render(Number(this.dataset.count || 0));
      document.addEventListener('cart:updated', (e) => {
        this.render(e.detail && e.detail.cart ? e.detail.cart.item_count : 0);
      });
    }
    render(count) {
      this.hidden = count <= 0;
      this.textContent = count;
    }
  }
  customElements.define('cart-count', CartCount);

  /* ------------------------------------------------------------
     <mobile-nav-toggle> — toggles mobile menu visibility
     ------------------------------------------------------------ */
  class MobileNavToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.target = document.getElementById(this.dataset.target);
      if (!this.button || !this.target) return;
      this.button.addEventListener('click', () => this.toggle());
    }
    toggle() {
      const open = this.target.hasAttribute('hidden');
      if (open) this.target.removeAttribute('hidden');
      else this.target.setAttribute('hidden', '');
      this.button.setAttribute('aria-expanded', String(open));
    }
  }
  customElements.define('mobile-nav-toggle', MobileNavToggle);

  /* ------------------------------------------------------------
     <product-form> — async add-to-cart
     ------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const submit = this.form.querySelector('[type="submit"]');
      const original = submit && submit.textContent;
      if (submit) { submit.disabled = true; submit.textContent = '...'; }
      let success = false;
      try {
        const data = new FormData(this.form);
        const items = { id: data.get('id'), quantity: Number(data.get('quantity') || 1) };
        await Cart.add(items);
        const cart = await Cart.get();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('cart:open'));
        success = true;
        if (submit) {
          submit.textContent = strings.added || 'Added ✓';
          submit.classList.add('is-added');
        }
      } catch (err) {
        console.error(err);
        if (submit) submit.textContent = strings.unavailable || 'Try again';
      } finally {
        if (submit) {
          setTimeout(() => {
            submit.disabled = false;
            submit.textContent = original;
            submit.classList.remove('is-added');
          }, success ? 1500 : 1200);
        }
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------
     Money formatting — uses Shopify's money_format string
     ------------------------------------------------------------ */
  function formatMoney(cents) {
    const fmt = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
    const value = Number(cents) / 100;
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, value.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(value).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.toFixed(2).replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(value).toString());
  }

  function renderPriceHTML(variant) {
    const price = formatMoney(variant.price);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const compare = formatMoney(variant.compare_at_price);
      return '<span class="main-product__price-sale">' + price + '</span> ' +
             '<span class="main-product__price-compare">' + compare + '</span>';
    }
    return '<span class="main-product__price-current">' + price + '</span>';
  }

  /* ------------------------------------------------------------
     <variant-picker> — radio inputs that drive the PDP buy box
     ------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-mp-variants-json]');
      if (!dataEl) return;
      try { this.variants = JSON.parse(dataEl.textContent); }
      catch (e) { return; }

      this.inputs = Array.from(this.querySelectorAll('[data-mp-option-input]'));
      this.formIdInput = document.querySelector('[data-mp-form-id]');
      this.priceEl = document.querySelector('[data-mp-price]');
      this.submitBtn = document.querySelector('[data-mp-submit]');

      this.inputs.forEach((i) => i.addEventListener('change', () => this.onChange()));
    }

    onChange() {
      const selected = this.getSelectedOptions();
      const variant = this.variants.find((v) =>
        v.options.length === selected.length &&
        v.options.every((opt, i) => opt === selected[i])
      );

      this.updateOptionLabels();

      if (!variant) {
        if (this.submitBtn) {
          this.submitBtn.disabled = true;
          this.submitBtn.textContent = strings.unavailable || 'Unavailable';
          this.submitBtn.classList.remove('is-added');
        }
        return;
      }

      if (this.formIdInput) this.formIdInput.value = variant.id;

      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (this.priceEl) this.priceEl.innerHTML = renderPriceHTML(variant);

      if (this.submitBtn) {
        this.submitBtn.disabled = !variant.available;
        this.submitBtn.textContent = variant.available
          ? (strings.addToCart || 'Add to cart')
          : (strings.soldOut || 'Sold out');
        this.submitBtn.classList.remove('is-added');
      }

      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant,
          mediaId: variant.featured_image && variant.featured_image.id
        }
      }));
    }

    getSelectedOptions() {
      const groups = {};
      this.inputs.forEach((i) => {
        if (i.checked) groups[i.dataset.mpOptionInput] = i.value;
      });
      return Object.keys(groups).sort().map((k) => groups[k]);
    }

    updateOptionLabels() {
      this.inputs.forEach((i) => {
        if (!i.checked) return;
        const idx = i.dataset.mpOptionInput;
        const label = this.querySelector('[data-mp-option-value="' + idx + '"]');
        if (label) label.textContent = i.value;
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------
     <product-media> — swap the visible PDP gallery slide
     ------------------------------------------------------------ */
  class ProductMedia extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-mp-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-mp-thumb]'));
      this.thumbs.forEach((t) => {
        t.addEventListener('click', () => this.show(t.dataset.mpMediaId));
      });
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.mediaId) this.show(String(e.detail.mediaId));
      });
    }
    show(id) {
      if (!id) return;
      let found = false;
      this.slides.forEach((s) => {
        const match = s.dataset.mpMediaId === id;
        s.hidden = !match;
        if (match) found = true;
      });
      if (!found) return;
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.mpMediaId === id));
    }
  }
  customElements.define('product-media', ProductMedia);

  /* ------------------------------------------------------------
     <newsletter-popup> — delayed, frequency-capped email modal
     ------------------------------------------------------------ */
  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.storeKey = '208golf:newsletter-popup';
      this.delay = Number(this.dataset.delay) || 0;
      this.frequency = Number(this.dataset.frequency) || 14;
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.querySelectorAll('[data-popup-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDialog());
      });
      this.dialog.addEventListener('click', (e) => this.onDialogClick(e));
      this.dialog.addEventListener('close', () => this.onClose());

      // The customer form does a full-page POST. On the reloaded page,
      // open straight into whichever state Liquid rendered.
      if (this.querySelector('[data-popup-success]')) {
        this.remember('subscribed');
        this.show();
        return;
      }
      if (this.querySelector('[data-popup-error]')) {
        this.show();
        return;
      }

      // Theme editor: always show so the merchant can style it.
      if (this.designMode) {
        this.show();
        document.addEventListener('shopify:section:select', (e) => {
          if (e.target.contains(this)) this.show();
        });
        return;
      }

      if (this.shouldShow()) {
        this.timer = window.setTimeout(() => this.show(), this.delay);
      }
    }

    disconnectedCallback() { window.clearTimeout(this.timer); }

    show() {
      if (this.dialog.open) return;
      // Lock the page in place BEFORE opening. showModal() autofocuses inside the
      // dialog and, because this section sits at the bottom of the DOM, the browser
      // would scroll the page down to it. A fixed-body lock makes the document
      // physically unscrollable while open, so neither open nor close can jump.
      this.lockScroll();
      this.dialog.showModal();
    }

    onClose() {
      this.unlockScroll();
      if (this.designMode) return;
      if (this.readStatus() !== 'subscribed') this.remember('dismissed');
    }

    lockScroll() {
      this.lockedY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = '-' + this.lockedY + 'px';
      document.documentElement.classList.add('newsletter-popup-open');
    }
    unlockScroll() {
      if (!document.documentElement.classList.contains('newsletter-popup-open')) return;
      document.documentElement.classList.remove('newsletter-popup-open');
      document.body.style.top = '';
      window.scrollTo(0, this.lockedY || 0);
    }

    // A click on the ::backdrop targets the <dialog> itself; inner content
    // targets a descendant. Coordinates outside the box confirm the backdrop.
    onDialogClick(e) {
      if (e.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      const outside =
        e.clientX < r.left || e.clientX > r.right ||
        e.clientY < r.top || e.clientY > r.bottom;
      if (outside) this.closeDialog();
    }

    closeDialog() {
      this.dialog.close();
    }

    shouldShow() {
      const rec = this.readRecord();
      if (!rec) return true;
      if (rec.status === 'subscribed') return false;
      return (Date.now() - rec.ts) / 86400000 >= this.frequency;
    }

    readRecord() {
      try { return JSON.parse(window.localStorage.getItem(this.storeKey)); }
      catch (e) { return null; }
    }
    readStatus() {
      const rec = this.readRecord();
      return rec && rec.status;
    }
    remember(status) {
      try {
        window.localStorage.setItem(
          this.storeKey,
          JSON.stringify({ status: status, ts: Date.now() })
        );
      } catch (e) { /* private mode — skip */ }
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);

  /* ------------------------------------------------------------
     Bootstrap: hydrate cart count on load
     ------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      const cart = await Cart.get();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (_) { /* silent */ }
  });
})();
